import { useQuery } from '@tanstack/react-query';

import { getSubscriptionGroups } from '@/services/subscription-groups';
import { Class } from './class-groups';
import { EntityImage } from '@/lib/entity-image';

export type SubscriptionAvailability = 'PUBLIC' | 'PRIVATE' | 'UNAVAILABLE';
export type RenewalClassesBehavior = 'RESET' | 'ROLLOVER' | 'LEAVE_AS_IS';
export type ChargeEveryInterval =
  | 'WEEK'
  | 'MONTH'
  | 'QUARTER'
  | 'SEMI_ANNUAL'
  | 'YEAR';

export interface Subscription {
  subscription_id: string;
  subscription_group_id: string;

  status: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  availability: SubscriptionAvailability;
  order: number | null;

  name: string;
  short_description: string;
  description: string | null;
  image: string | null;
  images?: EntityImage[];
  slug: string;

  price: number | null;
  currency: string | null;

  charge_every_count: number;
  charge_every_interval: ChargeEveryInterval;
  number_of_billing_cycles: number | null;
  total_number_of_classes_per_cycle: number | null;
  renewal_classes_behavior: RenewalClassesBehavior;

  one_time_purchase_only: boolean;
  new_customers_only: boolean;
  is_popular: boolean;
  is_custom: boolean;
  // Guests per billing cycle; null while guest passes are off or the plan
  // isn't eligible.
  guest_passes?: number | null;
  guest_pass_new_guests_only?: boolean;

  subscription_classes?: {
    subscription_class_id: string;
    subscription_id: string;
    class_id: string;
    class?: Pick<Class, 'name' | 'slug'>;
  }[];
}

export interface SubscriptionGroup {
  subscription_group_id: string;
  admin_user_id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  slug: string;
  // Not sent by the public list, which returns ACTIVE groups only.
  status?: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  order: number;
  subscriptions: Subscription[];
}

// class_id narrows the list to the subscriptions that cover that class.
export default function useSubscriptionGroups({
  include_subscriptions,
  class_id,
  enabled,
}: {
  include_subscriptions: boolean;
  class_id?: string;
  enabled?: boolean;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptionGroups', include_subscriptions, class_id ?? null],
    queryFn: () => getSubscriptionGroups({ include_subscriptions, class_id }),
    enabled,
    // The home page tabs wait on this and the tab inside observes it, so the
    // same loop as useClassGroups.
    retryOnMount: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
